import * as z from 'zod/mini'

export const SEARCH_MAX_LENGTH = 100

const searchQuery = z._default(z.string().check(z.maxLength(SEARCH_MAX_LENGTH)), '')

export const baseDashboardSchema = z.object({
	ecosystem: z._default(z.boolean(), true),
	gainers: z._default(z.boolean(), false),
	l1: z._default(z.boolean(), true),
	l2: z._default(z.boolean(), true),
	mainnet: z._default(z.boolean(), true),
	order: z._default(z.enum(['asc', 'desc']), 'desc'),
	q: searchQuery,
	sidechain: z._default(z.boolean(), true),
	testnet: z._default(z.boolean(), true),
})

export const agentsSchema = z.object({
	order: z._default(z.enum(['asc', 'desc']), 'desc'),
	page: z._default(z.int(), 1),
	pageSize: z._default(z.int().check(z.lte(100)), 20),
	q: searchQuery,
	sort: z._default(z.enum(['name', 'id', 'owner', 'x402', 'created']), 'id'),
})

export const cardsSchema = z.object({
	q: searchQuery,
	sort: z._default(
		z.enum(['name', 'cashback', 'fxFee', 'annualFee', 'kyc', 'borrowing', 'launched']),
		'cashback'
	),
	order: z._default(z.enum(['asc', 'desc']), 'desc'),
})

export const cardMetricsSchema = z.object({
	q: searchQuery,
	'range-cm': z._default(z.enum(['day', 'month', 'week']), 'day'),
	sort: z._default(
		z.enum([
			'name',
			'volume',
			'txns',
			'users',
			'total_volume',
			'total_txns',
			'total_users',
			'launched',
		]),
		'volume'
	),
	order: z._default(z.enum(['asc', 'desc']), 'desc'),
})

export const decentralizationSchema = z.intersection(
	baseDashboardSchema,
	z.object({
		sort: z._default(z.enum(['name', 'nc', 'validatorsOrMiners', 'stakeOrHashrate']), 'nc'),
		stake: z._default(z.enum(['usd', 'native']), 'usd'),
		page: z._default(z.int().check(z.gte(1)), 1),
		pageSize: z._default(z.int().check(z.gte(1), z.lte(50)), 15),
	})
)

export const devActivitySchema = z.intersection(
	baseDashboardSchema,
	z.object({
		'range-dm': z._default(z.enum(['week', 'month', 'all-time']), 'all-time'),
		sort: z._default(
			z.enum(['name', 'developers', 'repos', 'commits', 'stars', 'watchers']),
			'developers'
		),
		page: z._default(z.int().check(z.gte(1)), 1),
		pageSize: z._default(z.int().check(z.gte(1), z.lte(50)), 15),
	})
)

export const financialsSchema = z.intersection(
	baseDashboardSchema,
	z.object({
		sort: z._default(
			z.enum(['name', 'revenue', 'avgTxFee', 'price', 'marketCap', 'fdv', 'marketCapToFdv']),
			'revenue'
		),
		'range-fm': z._default(z.enum(['day', 'week', 'month']), 'day'),
		revenue: z._default(z.enum(['usd', 'native']), 'usd'),
		avgTxFee: z._default(z.enum(['usd', 'native']), 'usd'),
		page: z._default(z.int().check(z.gte(1)), 1),
		pageSize: z._default(z.int().check(z.gte(1), z.lte(50)), 15),
	})
)

export const scalabilitySchema = z.intersection(
	baseDashboardSchema,
	z.object({
		'max-tps': z._default(z.enum(['one-block', 'hundred-blocks']), 'hundred-blocks'),
		'range-cm': z._default(z.enum(['hour', 'day', 'week', 'month']), 'hour'),
		launched: z._default(z.enum(['age', 'date']), 'date'),
		sort: z._default(
			z.enum([
				'name',
				'tps',
				'maxTps',
				'theorTps',
				'txns',
				'totalTxns',
				'blocktime',
				'ttf',
				'launched',
			]),
			'tps'
		),
		page: z._default(z.int().check(z.gte(1)), 1),
		pageSize: z._default(z.int().check(z.gte(1), z.lte(50)), 15),
	})
)

// Line chart metrics, in their menu order
export const LINE_CHART_METRICS = [
	'tps',
	'txns',
	'blocktime',
	'nc',
	'stake',
	'stake_native',
	'validators',
	'hashrate',
	'miners',
	'developers',
	'commits',
	'repos',
	'stars',
	'watchers',
	'revenue',
	'revenue_native',
	'avg_tx_fee',
	'avg_tx_fee_native',
	'price',
	'market_cap',
] as const

export const chainSchema = z.object({
	'range-am': z._default(z.enum(['day', 'week', 'month', 'all-time']), 'all-time'),
	'range-cm': z._default(z.enum(['hour', 'day', 'week', 'month']), 'hour'),
	'range-dm': z._default(z.enum(['week', 'month', 'all-time']), 'all-time'),
	'range-fm': z._default(z.enum(['day', 'week', 'month']), 'day'),
	avgTxFee: z._default(z.enum(['usd', 'native']), 'usd'),
	launched: z._default(z.enum(['age', 'date']), 'date'),
	'max-tps': z._default(z.enum(['one-block', 'hundred-blocks']), 'hundred-blocks'),
	revenue: z._default(z.enum(['usd', 'native']), 'usd'),
	stake: z._default(z.enum(['usd', 'native']), 'usd'),
	'type-tc': z._default(z.enum(LINE_CHART_METRICS), 'tps'),
	'range-tc': z._default(z.enum(['week', 'month', 'quarter']), 'week'),
	s: z._default(z.boolean(), false),
})

// Picked chain slugs; empty means the default (top 10 on the bar chart, all on the time chart)
const chainsParam = z._default(z.array(z.string()), [])

export const BAR_CHART_METRICS = [
	'tps',
	'maxTps',
	'maxTpsOneBlock',
	'theorTps',
	'txns',
	'totalTxns',
	'blocktime',
	'ttf',
	'nc',
	'validators',
	'miners',
	'stake',
	'hashrate',
	'developers',
	'repos',
	'commits',
	'stars',
	'watchers',
	'revenue',
	'avgTxFee',
	'price',
	'marketCap',
	'fdv',
	'marketCapToFdv',
] as const

export const barChartSchema = z.object({
	metric: z._default(z.enum(BAR_CHART_METRICS), 'tps'),
	'range-cm': chainSchema.shape['range-cm'],
	'range-dm': chainSchema.shape['range-dm'],
	'range-fm': chainSchema.shape['range-fm'],
	chains: chainsParam,
})

// Time chart metrics, in their menu order. Values are `chart_time` column names
export const TIME_CHART_METRICS = ['ttf', 'block_time'] as const

export const timeChartSchema = z.object({
	metric: z._default(z.enum(TIME_CHART_METRICS), 'ttf'),
	chains: chainsParam,
})

export const agentActivitySchema = z.intersection(
	baseDashboardSchema,
	z.object({
		'range-am': z._default(z.enum(['day', 'week', 'month', 'all-time']), 'all-time'),
		sort: z._default(z.enum(['name', 'agents', 'activeAgents', 'x402', 'servicesN']), 'agents'),
		page: z._default(z.int().check(z.gte(1)), 1),
		pageSize: z._default(z.int().check(z.gte(1), z.lte(50)), 15),
	})
)
